import { find, get, map } from 'lodash'
import routes from '~/const/routes'
import { isDocumentsApiV2 } from '~/modules/auth/utils/user'

export const state = () => ({
  // new
  items: [],
  meta: {
    totalItems: null
  },
  currentDocument: null,
  requestParams: null,
  documentScale: 1,
  shouldRerenderDocument: false,
  filters: {}
})

export const getters = {
  // new
  items: state => state.items,
  currentDocument: state => state.currentDocument,
  meta: state => state.meta,
  // old
  requestParams: state => state.requestParams,
  documentScale: state => state.documentScale,
  shouldRerenderDocument: state => state.shouldRerenderDocument,
  filters: state => state.filters
}

export const mutations = {
  AFTER_DOCUMENT_READ(state, item) {
    const isUpdate = find(state.items, i => i.id === item.id)
    if (isUpdate) {
      state.items = map(state.items, i => {
        if (i.id === item.id) {
          return { ...i, ...item }
        } else {
          return i
        }
      })
    } else {
      state.items.push(item)
    }
  },
  SET_DOCUMENT_SCALE(state, scale) {
    state.documentScale = scale
  },
  SET_SHOULD_RERENDER_DOCUMENT(state, value) {
    state.shouldRerenderDocument = value
  },
  SET_FILTERS(state, value) {
    state.filters = value
  },
  SET_CURRENT_DOCUMENT(state, document) {
    state.currentDocument = document
  },
  UPDATE_DOCUMENT(state, { id, data }) {
    state.items = map(state.items, doc => (doc.id === id ? { ...doc, ...data } : doc))
    if (id === state.currentDocument?.id) {
      state.currentDocument = { ...state.currentDocument, ...data }
    }
  },
  RESET(state) {
    state.items = []
    state.meta = { totalItems: null }
    state.requestParams = null
    state.filters = {}
    state.currentDocument = null
  }
}

export const actions = {
  // CRUD operations
  async getAll({ state, commit }, { config = {} } = {}) {
    const { data, meta } = get(
      await this.$axios.get(isDocumentsApiV2() ? routes.document.listV2() : routes.document.list(), {
        params: {
          ...state.requestParams
        },
        ...config
      }),
      'data'
    )

    commit(
      'AFTER_GET_ALL',
      {
        namespace: 'documents',
        items: data,
        meta,
        params: {
          ...state.requestParams,
          ...config.params
        }
      },
      { root: true }
    )

    return { data, meta }
  },
  async create({ commit }, { payload = {}, save = true, config = {} }) {
    const data = get(await this.$axios.post(routes.document.list(), payload, config), 'data')

    if (save) {
      commit(
        'AFTER_CREATE',
        {
          namespace: 'documents',
          item: data
        },
        { root: true }
      )
    }

    return data
  },
  async read({ state, commit }, { id, config }) {
    const document = get(await this.$axios.get(routes.document.concrete(id), config), 'data')
    commit('AFTER_DOCUMENT_READ', document)
    const currentDocument = find(state.items, item => item.id === id)
    commit('SET_CURRENT_DOCUMENT', currentDocument)
    return document
  },
  async update({ commit }, { id, payload = {}, config = {} }) {
    const data = get(await this.$axios.put(routes.document.concrete(id), payload, config), 'data')
    commit(
      'AFTER_UPDATE',
      {
        namespace: 'documents',
        item: data
      },
      { root: true }
    )
    return data
  },
  async delete({ commit }, { id, config = {} }) {
    const response = await this.$axios.delete(routes.document.concrete(id), config)
    commit('AFTER_DELETE', { namespace: 'documents', id }, { root: true })
    return response
  },
  // documents common requests
  sign({ commit }, { id, payload = {}, config = {} }) {
    return this.$axios.post(routes.document.sign(id), payload, config)
  },
  send({ commit }, { id, payload = {} }) {
    return this.$axios.post(routes.document.send(id), payload)
  },
  toArchive({ commit }, id) {
    return this.$axios.post(
      routes.document.toArchive(id),
      {},
      {
        // TODO: move it on axios plugin level
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
  },
  fromArchive({ commit }, id) {
    return this.$axios.post(
      routes.document.fromArchive(id),
      {},
      {
        // TODO: move it on axios plugin level
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
  },
  convertToPdf({ commit }, id) {
    return this.$axios.post(
      routes.document.convertToPdf(id),
      {},
      {
        // TODO: move it on axios plugin level
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
  },
  downloadXml({ commit }, url) {
    return this.$axios.get(url, {
      responseType: 'text'
    })
  },
  cancelDocument({ commit }, { id, payload = {} }) {
    return this.$axios.post(routes.document.cancel(id), payload, {
      // TODO: move it on axios plugin level
      headers: {
        'Content-Type': 'application/json'
      }
    })
  },
  generateDocumentLink({ commit }, { id, payload }) {
    return this.$axios.post(routes.document.documentLink(id), payload)
  },
  deleteDocumentLink({ commit }, id) {
    return this.$axios.delete(routes.document.documentLink(id))
  },
  submitFieldValues({ commit }, { id, payload, config = {} }) {
    return this.$axios.post(routes.document.fillDynamicFields(id), payload, config)
  },
  addTags({ commit }, { id, payload }) {
    return this.$axios.post(routes.document.tags(id), payload)
  },
  deleteTag({ commit }, { id, tagId }) {
    return this.$axios.delete(routes.document.concreteTag(id, tagId))
  },
  attachTags({ commit }, payload) {
    return this.$axios.post(routes.document.attachTags(), payload)
  },
  download({ commit }, { id, params }) {
    return this.$axios.get(routes.document.download(id), {
      responseType: 'blob',
      params
    })
  },
  downloadDocuments({ commit }, payload) {
    return this.$axios.post(routes.document.downloadDocuments(), payload)
  },
  downloadArchive({ commit }, id) {
    return this.$axios.get(routes.document.downloadArchive(id), {
      responseType: 'blob'
    })
  },
  checkStatus({ commit }, id) {
    return this.$axios.get(routes.document.downloadStatus(id))
  },
  archiveDocuments({ commit }, payload) {
    return this.$axios.post(routes.document.archive(), payload)
  },
  shareDocuments({ commit }, payload) {
    return this.$axios.post(routes.document.documentsParticipants(), payload)
  },
  reject({ commit }, { id, payload }) {
    return this.$axios.post(routes.document.reject(id), payload)
  },
  logs({ commit }, id) {
    return this.$axios.get(routes.document.logs(id))
  },
  sendOnApprovals({ commit }, { id, payload }) {
    return this.$axios.post(routes.document.approvals(id), payload)
  },
  approve({ commit }, id) {
    return this.$axios.post(routes.document.approve(id))
  },
  relateDocuments({ commit }, { id, payload }) {
    return this.$axios.post(routes.document.relations(id), payload)
  },
  deleteRelatedDocument({ commit }, { id, relatedDocumentId }) {
    return this.$axios.delete(routes.document.concreteRelations(id, relatedDocumentId))
  }
}
